import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Check, Mail, Trash2 } from "lucide-react";
import { apiFetch, API } from "@/utils/api";
import { DEFAULT_SUPPLIER_CATEGORIES } from "@/utils/supplierCategories";
import { useSupplierCategories } from "@/hooks/useSupplierCategories";
import SupplierCategorySuggestion from "@/components/SupplierCategorySuggestion";

// Export legacy (3.6): las 4 categorías históricas como constante de fallback.
// El catálogo real por tenant llega vía useSupplierCategories().
export const SUPPLIER_CATEGORIES = DEFAULT_SUPPLIER_CATEGORIES;

// Confianza OCR: documents.ocr_result.confidence = {campo: "ok"|"low", "_summary": "ok"|"low"}
function OcrLowWarning() {
  return (
    <span
      className="ml-1 text-amber-600"
      title="Dato dudoso — revísalo"
      role="img"
      aria-label="Dato dudoso — revísalo"
    >
      ⚠
    </span>
  );
}

export default function OCRPreviewDialog({ document, queueRemaining = 1, onCreateInvoice, onClose, onDiscard }) {
  const ocrData = document?.ocr_result || {};
  const confidence = ocrData.confidence || {};
  const isLow = (field) => confidence[field] === "low";
  // Anillo ámbar para valores extraídos con poca confianza
  const lowClass = (field) => (isLow(field) ? " rounded border border-amber-400 ring-1 ring-amber-300 bg-amber-50/50 px-1" : "");
  const currency = ocrData.currency || "EUR";
  const currencySymbol = { EUR: "€", USD: "$", GBP: "£", CHF: "CHF", JPY: "¥" }[currency] || currency;
  const [submitting, setSubmitting] = useState(false);
  const [supplierCategory, setSupplierCategory] = useState("");
  // 3.6: propuesta completa {category, source, confidence?, reason?} para la
  // línea de fuente (patrón 3.4). ai=true → si no hay señal determinista y el
  // tenant consintió, el backend pregunta a la IA (proveedor nuevo).
  const [categoryProposal, setCategoryProposal] = useState(null);
  const supplierCategories = useSupplierCategories();

  // Ingesta por correo: un PDF que no es factura se descarta desde aquí mismo
  const discardButton = document?.source === "email" && onDiscard ? (
    <Button
      variant="ghost"
      className="mr-auto text-red-600 hover:text-red-700 hover:bg-red-50"
      onClick={() => onDiscard(document.document_id)}
      disabled={submitting}
      data-testid="discard-email-doc-btn"
    >
      <Trash2 className="w-4 h-4 mr-2" />
      Descartar (no es factura)
    </Button>
  ) : null;

  // Pre-rellena la categoría propuesta al montar (si hay supplier_name)
  useEffect(() => {
    const name = ocrData.supplier_name;
    if (!name) return;
    apiFetch(`${API}/supplier-categories/propose?supplier_name=${encodeURIComponent(name)}&ai=true`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.category) {
          setSupplierCategory(data.category);
          setCategoryProposal(data);
        }
      })
      .catch(() => {});
  }, [ocrData.supplier_name]);

  const handleCreate = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onCreateInvoice(document.document_id, supplierCategory || null);
    } finally {
      setSubmitting(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: currency,
    }).format(amount || 0);
  };

  return (
    <DialogContent
      className="max-w-2xl"
      onInteractOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-500" />
          Datos extraídos por OCR
          {queueRemaining > 1 && (
            <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              {queueRemaining} en cola
            </span>
          )}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        {document?.source === "email" && (
          <div
            className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900"
            data-testid="email-source-block"
          >
            <Mail className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="font-medium">Llegó por correo</div>
              <div className="truncate">
                {document.source_meta?.from || "remitente desconocido"} · {document.source_meta?.subject || "(sin asunto)"}
                {document.source_meta?.date ? ` · ${new Date(document.source_meta.date).toLocaleDateString("es-ES")}` : ""}
              </div>
            </div>
          </div>
        )}
        {document?.ocr_status === "completed" && !ocrData.parse_error ? (
          <>
            {confidence._summary === "low" && (
              <div
                className="rounded-md bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 text-sm"
                data-testid="ocr-low-confidence-banner"
              >
                Algunos datos se han extraído con poca confianza. Revísalos antes de crear la factura.
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Proveedor{isLow("supplier_name") && <OcrLowWarning />}</Label>
                <p className={`font-medium${lowClass("supplier_name")}`}>{ocrData.supplier_name || "-"}</p>
              </div>
              <div>
                <Label className="text-slate-500">NIF{isLow("supplier_nif") && <OcrLowWarning />}</Label>
                <p className={`font-medium${lowClass("supplier_nif")}`}>{ocrData.supplier_nif || "-"}</p>
              </div>
              <div>
                <Label className="text-slate-500">Nº Factura{isLow("invoice_number") && <OcrLowWarning />}</Label>
                <p className={`font-medium${lowClass("invoice_number")}`}>{ocrData.invoice_number || "-"}</p>
              </div>
              <div>
                <Label className="text-slate-500">Fecha{isLow("invoice_date") && <OcrLowWarning />}</Label>
                <p className={`font-medium${lowClass("invoice_date")}`}>{ocrData.invoice_date || "-"}</p>
              </div>
              <div>
                <Label className="text-slate-500">Moneda Detectada{isLow("currency") && <OcrLowWarning />}</Label>
                <p className="font-medium">
                  <Badge variant="outline" className={`text-base${isLow("currency") ? " border-amber-400 ring-1 ring-amber-300 bg-amber-50/50" : ""}`}>
                    {currency} {currencySymbol}
                  </Badge>
                </p>
              </div>
              <div>
                <Label className="text-slate-500">Tipo de proveedor{isLow("supplier_type") && <OcrLowWarning />}</Label>
                <p className="font-medium">
                  <Badge
                    variant="outline"
                    className={
                      ocrData.supplier_type === "intracomunitario"
                        ? "bg-amber-50 text-amber-700 border-amber-200"
                        : ocrData.supplier_type === "extracomunitario"
                        ? "bg-violet-50 text-violet-700 border-violet-200"
                        : "bg-slate-50 text-slate-700 border-slate-200"
                    }
                  >
                    {ocrData.supplier_type || "nacional"}
                  </Badge>
                  {ocrData.reverse_charge && (
                    <Badge
                      variant="outline"
                      className="ml-2 bg-amber-100 text-amber-700 border-amber-200"
                      title="El IVA se autoliquida en el 303 (21% soportado + 21% repercutido, efecto neto 0)."
                    >
                      Autoliquidación
                    </Badge>
                  )}
                </p>
              </div>
              <div>
                <Label className="text-slate-500">IVA ({ocrData.vat_rate || 0}%){(isLow("vat_amount") || isLow("vat_rate")) && <OcrLowWarning />}</Label>
                <p className={`font-mono font-medium${lowClass("vat_amount")}`}>{formatAmount(ocrData.vat_amount)}</p>
              </div>
              <div>
                <Label className="text-slate-500">Base Imponible{isLow("base_amount") && <OcrLowWarning />}</Label>
                <p className={`font-mono font-medium${lowClass("base_amount")}`}>{formatAmount(ocrData.base_amount)}</p>
              </div>
              {(ocrData.irpf_amount || ocrData.irpf_rate) ? (
                <div>
                  <Label className="text-slate-500">Retención IRPF ({ocrData.irpf_rate || 0}%){(isLow("irpf_amount") || isLow("irpf_rate")) && <OcrLowWarning />}</Label>
                  <p className={`font-mono font-medium text-rose-600${lowClass("irpf_amount")}`} data-testid="ocr-irpf-amount">
                    −{formatAmount(Math.abs(ocrData.irpf_amount || 0))}
                  </p>
                </div>
              ) : null}
              <div>
                <Label className="text-slate-500">Total{isLow("total_amount") && <OcrLowWarning />}</Label>
                <p className={`font-mono text-xl font-semibold${lowClass("total_amount")}`}>{formatAmount(ocrData.total_amount)}</p>
              </div>
            </div>

            <div>
              <Label className="text-slate-500">Categoría del proveedor</Label>
              <Select value={supplierCategory} onValueChange={setSupplierCategory}>
                <SelectTrigger data-testid="ocr-supplier-category-select">
                  <SelectValue placeholder="Selecciona una categoría..." />
                </SelectTrigger>
                <SelectContent>
                  {/* Si la propuesta (histórico) no está en el catálogo cargado,
                      se añade como opción para que el select nunca quede mudo */}
                  {(supplierCategory && !supplierCategories.includes(supplierCategory)
                    ? [...supplierCategories, supplierCategory]
                    : supplierCategories
                  ).map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <SupplierCategorySuggestion proposal={categoryProposal} />
            </div>

            <DialogFooter>
              {discardButton}
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                Cancelar
              </Button>
              <Button onClick={handleCreate} disabled={submitting} data-testid="create-from-ocr-btn">
                <Check className="w-4 h-4 mr-2" />
                {submitting ? "Creando..." : "Crear Factura"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <p>Error al procesar el documento</p>
            <p className="text-sm mt-2">Crea la factura manualmente</p>
            <div className="mt-6 flex justify-end gap-2">
              {discardButton}
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
            </div>
          </div>
        )}
      </div>
    </DialogContent>
  );
}
